import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { EVENT, makeCustomEvent } from '../util/events';

@customElement('rq-checkbox')
export class Checkbox extends LitElement {
    static override styles = css`
        :host,
        .wrapper {
            display: flex;
            gap: 12px;
            align-items: center;
            font-family: var(--lh-font-family-sans-serif);
            -webkit-font-smoothing: antialiased;
            cursor: pointer;
            color: var(--lh-light-typography-text, #222425);
        }
        .disabled {
            color: var(--lh-color-neutral-500);
            cursor: default;
        }
        .icon {
            border: 1px solid var(--Neutral-500, #919799);
            background-color: white;
            width: 18px;
            height: 18px;
            color: var(--mode-color-primary-900);
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 2px;
        }
        .icon:focus-within {
            outline: none;
            box-shadow: var(--cf-box-shadow, 0px 0px 0px 2px var(--lh-color-primary-200));
        }
        .hide {
            visibility: hidden;
        }
        .show {
            visibility: visible;
        }
    `;

    @property({ type: Boolean })
    checked = false;

    @property({ type: Boolean })
    disabled = false;

    @property({ type: String })
    name = '';

    @property({ type: String })
    value = '';

    connectedCallback(): void {
        super.connectedCallback();
        this.style.setProperty(
            '--cf-box-shadow',
            this.disabled ? 'none' : '0px 0px 0px 2px var(--lh-color-primary-200)'
        );
    }

    private dispatch() {
        if (this.disabled) return;
        this.dispatchEvent(makeCustomEvent(EVENT.CHECKBOX_CLICK, { name: this.name, value: this.value }));
    }

    private onClick() {
        this.dispatch();
    }

    private onKeyUp(e: KeyboardEvent) {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        this.dispatch();
    }

    render() {
        return html`<div
            role="checkbox"
            @click="${this.onClick}"
            @keyup="${this.onKeyUp}"
            class=${classMap({ wrapper: true, disabled: this.disabled })}
        >
            <div tabindex="${this.disabled ? '-1' : '0'}" class="icon">
                <lh-icon
                    class="${classMap({ icon: true, hide: !this.checked, show: this.checked })}"
                    data-testid="check-icon"
                    name="check-bold"
                    size="xs"
                >
                </lh-icon>
            </div>
            <label><slot></slot></label>
        </div>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'rq-checkbox': Checkbox;
    }
}
